import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Card } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { 
  Select, SelectContent, SelectItem, 
  SelectTrigger, SelectValue 
} from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, Plus, X, FileText, Mic, Video, 
  Settings, Code, ListChecks, PlayCircle, Loader2,
  AlertTriangle, CheckCircle
} from 'lucide-react';

export default function CustomSkillEditor() {
  const { t } = useTranslation();
  const { skillPackageId } = useParams();
  const navigate = useNavigate();
  const isEditing = skillPackageId && skillPackageId !== 'new';
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basics');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Preview state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [testMessage, setTestMessage] = useState('');
  
  // Original data for change detection
  const [originalData, setOriginalData] = useState(null);
  
  // Skill package data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Productivity',
    tags: [],
    visibility: 'private',
    riskLevel: 'low',
    requirements: {
      text: true,
      voice: false,
      video: false
    },
    capabilities: []
  });

  // Skill prompt data
  const [promptData, setPromptData] = useState({
    systemPrompt: '',
    jobDescription: '',
    priorityRules: [],
    outputConstraints: {
      tone: 'professional',
      maxWords: null,
      formatRules: []
    },
    isActionBased: false,
    actionTypes: []
  });
  
  const [hasPrompt, setHasPrompt] = useState(false);
  const [skillVersion, setSkillVersion] = useState('1.0.0');

  const [tagInput, setTagInput] = useState('');
  const [capabilityInput, setCapabilityInput] = useState('');
  const [ruleInput, setRuleInput] = useState('');
  const [formatRuleInput, setFormatRuleInput] = useState('');

  // Check for unsaved changes
  useEffect(() => {
    if (originalData) {
      const currentData = JSON.stringify({ formData, promptData });
      const original = JSON.stringify(originalData);
      setHasUnsavedChanges(currentData !== original);
    }
  }, [formData, promptData, originalData]);

  // Handle navigation blocking
  const handleNavigation = useCallback((path) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowLeaveDialog(true);
      return false;
    }
    navigate(path);
    return true;
  }, [hasUnsavedChanges, navigate]);

  useEffect(() => {
    if (isEditing) {
      fetchSkillFull();
    }
  }, [skillPackageId]);

  const fetchSkillFull = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/skills/custom/${skillPackageId}/full`);
      const { skill, prompt, hasPrompt: promptExists } = response.data;
      
      setFormData({
        name: skill.name,
        description: skill.description,
        category: skill.category,
        tags: skill.tags || [],
        visibility: skill.visibility,
        riskLevel: skill.riskLevel,
        requirements: skill.requirements,
        capabilities: skill.capabilities || []
      });
      
      setSkillVersion(skill.version || '1.0.0');
      setHasPrompt(promptExists);
      
      if (prompt) {
        setPromptData({
          systemPrompt: prompt.systemPrompt || '',
          jobDescription: prompt.jobDescription || '',
          priorityRules: prompt.priorityRules || [],
          outputConstraints: prompt.outputConstraints || {
            tone: 'professional',
            maxWords: null,
            formatRules: []
          },
          isActionBased: prompt.isActionBased || false,
          actionTypes: prompt.actionTypes || []
        });
      }
      
      // Store original for change detection
      setOriginalData({
        formData: {
          name: skill.name,
          description: skill.description,
          category: skill.category,
          tags: skill.tags || [],
          visibility: skill.visibility,
          riskLevel: skill.riskLevel,
          requirements: skill.requirements,
          capabilities: skill.capabilities || []
        },
        promptData: prompt ? {
          systemPrompt: prompt.systemPrompt || '',
          jobDescription: prompt.jobDescription || '',
          priorityRules: prompt.priorityRules || [],
          outputConstraints: prompt.outputConstraints || { tone: 'professional', maxWords: null, formatRules: [] },
          isActionBased: prompt.isActionBased || false,
          actionTypes: prompt.actionTypes || []
        } : {
          systemPrompt: '',
          jobDescription: '',
          priorityRules: [],
          outputConstraints: { tone: 'professional', maxWords: null, formatRules: [] },
          isActionBased: false,
          actionTypes: []
        }
      });
      
    } catch (error) {
      console.error('Failed to fetch skill:', error);
      toast.error(t('errors.loadFailed'));
      navigate('/personal/twin/custom-skills');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Skill name is required');
      setActiveTab('basics');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Skill description is required');
      setActiveTab('basics');
      return;
    }

    // Validate prompt if editing
    if (isEditing && promptData.systemPrompt && promptData.systemPrompt.length > 6000) {
      toast.error('System prompt must be 6000 characters or less');
      setActiveTab('behavior');
      return;
    }

    try {
      setSaving(true);
      
      if (isEditing) {
        // Update skill package
        await apiClient.put(`/skills/custom/${skillPackageId}`, formData);
        
        // Update or create prompt
        if (promptData.systemPrompt || promptData.jobDescription) {
          if (hasPrompt) {
            await apiClient.put(`/skills/prompts/${skillPackageId}`, promptData);
          } else {
            await apiClient.post('/skills/prompts', {
              skillPackageId,
              ...promptData
            });
            setHasPrompt(true);
          }
        }
        
        toast.success('Skill updated successfully');
        setHasUnsavedChanges(false);
        setOriginalData({ formData, promptData });
      } else {
        // Create new skill
        const newSkill = await apiClient.post('/skills/custom', formData);
        
        // Create prompt if provided
        if (promptData.systemPrompt || promptData.jobDescription) {
          await apiClient.post('/skills/prompts', {
            skillPackageId: newSkill.data.skillPackageId,
            ...promptData
          });
        }
        
        toast.success('Skill created successfully');
        navigate('/personal/twin/custom-skills');
      }
    } catch (error) {
      console.error('Failed to save skill:', error);
      toast.error(error.response?.data?.detail || t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!testMessage.trim()) {
      toast.error('Please enter a test message');
      return;
    }

    try {
      setPreviewLoading(true);
      setPreviewResult(null);
      
      const response = await apiClient.post(`/skills/custom/${skillPackageId}/preview`, {
        testMessage,
        // Use current unsaved values
        systemPrompt: promptData.systemPrompt || undefined,
        jobDescription: promptData.jobDescription || undefined,
        priorityRules: promptData.priorityRules.length > 0 ? promptData.priorityRules : undefined
      });
      
      setPreviewResult(response.data);
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error(error.response?.data?.detail || 'Preview test failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  // List management helpers
  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
  };

  const handleAddCapability = () => {
    if (capabilityInput.trim() && !formData.capabilities.includes(capabilityInput.trim())) {
      setFormData({ ...formData, capabilities: [...formData.capabilities, capabilityInput.trim()] });
      setCapabilityInput('');
    }
  };

  const handleRemoveCapability = (cap) => {
    setFormData({ ...formData, capabilities: formData.capabilities.filter(c => c !== cap) });
  };

  const handleAddRule = () => {
    if (ruleInput.trim() && promptData.priorityRules.length < 20) {
      setPromptData({ ...promptData, priorityRules: [...promptData.priorityRules, ruleInput.trim()] });
      setRuleInput('');
    }
  };

  const handleRemoveRule = (ruleToRemove) => {
    setPromptData({ ...promptData, priorityRules: promptData.priorityRules.filter(r => r !== ruleToRemove) });
  };

  const handleAddFormatRule = () => {
    if (formatRuleInput.trim() && promptData.outputConstraints.formatRules.length < 20) {
      setPromptData({
        ...promptData,
        outputConstraints: {
          ...promptData.outputConstraints,
          formatRules: [...promptData.outputConstraints.formatRules, formatRuleInput.trim()]
        }
      });
      setFormatRuleInput('');
    }
  };

  const handleRemoveFormatRule = (rule) => {
    setPromptData({
      ...promptData,
      outputConstraints: {
        ...promptData.outputConstraints,
        formatRules: promptData.outputConstraints.formatRules.filter(r => r !== rule)
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading skill...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="custom-skill-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => handleNavigation('/personal/twin/custom-skills')}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('common.back')}
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditing ? 'Edit Skill' : 'Create Custom Skill'}
            </h1>
            {isEditing && (
              <Badge variant="outline" className="text-xs">
                v{skillVersion}
              </Badge>
            )}
            {hasUnsavedChanges && (
              <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-700">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Unsaved changes
              </Badge>
            )}
          </div>
          <p className="text-gray-600">
            {isEditing ? 'Update your custom skill settings and behavior' : 'Build a skill tailored to your needs'}
          </p>
        </div>
        
        <Button
          onClick={handleSubmit}
          disabled={saving || (!hasUnsavedChanges && isEditing)}
          data-testid="save-skill-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditing ? 'Save Changes' : 'Create Skill'}
            </>
          )}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="basics" className="gap-2">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Basics</span>
          </TabsTrigger>
          <TabsTrigger value="behavior" className="gap-2">
            <Code className="w-4 h-4" />
            <span className="hidden sm:inline">Behavior</span>
          </TabsTrigger>
          <TabsTrigger value="rules" className="gap-2">
            <ListChecks className="w-4 h-4" />
            <span className="hidden sm:inline">Rules</span>
          </TabsTrigger>
          {isEditing && (
            <TabsTrigger value="preview" className="gap-2">
              <PlayCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Preview</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Basics Tab */}
        <TabsContent value="basics">
          <Card className="p-6 space-y-6">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Basic Information</h2>
              
              <div className="space-y-2">
                <Label htmlFor="name">Skill Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Email Composer, Meeting Notes Summarizer"
                  required
                  data-testid="skill-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what this skill does and when to use it..."
                  rows={4}
                  required
                  data-testid="skill-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select 
                    value={formData.category}
                    onValueChange={(val) => setFormData({ ...formData, category: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Writing">Writing</SelectItem>
                      <SelectItem value="Support">Support</SelectItem>
                      <SelectItem value="Productivity">Productivity</SelectItem>
                      <SelectItem value="Development">Development</SelectItem>
                      <SelectItem value="Marketing">Marketing</SelectItem>
                      <SelectItem value="Research">Research</SelectItem>
                      <SelectItem value="Coaching">Coaching</SelectItem>
                      <SelectItem value="Education">Education</SelectItem>
                      <SelectItem value="Communication">Communication</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Risk Level</Label>
                  <Select 
                    value={formData.riskLevel}
                    onValueChange={(val) => setFormData({ ...formData, riskLevel: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Visibility</Label>
                  <Select 
                    value={formData.visibility}
                    onValueChange={(val) => setFormData({ ...formData, visibility: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="private">Private</SelectItem>
                      <SelectItem value="unlisted">Unlisted</SelectItem>
                      <SelectItem value="public">Public</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-4">
              <h3 className="text-base font-medium">Tags</h3>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Add a tag..."
                  className="max-w-xs"
                />
                <Button type="button" variant="outline" size="sm" onClick={handleAddTag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, idx) => (
                    <Badge key={idx} variant="secondary">
                      #{tag}
                      <button type="button" onClick={() => handleRemoveTag(tag)} className="ml-2 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Capabilities */}
            <div className="space-y-4">
              <h3 className="text-base font-medium">Capabilities</h3>
              <p className="text-sm text-gray-600">List what this skill can do</p>
              <div className="flex gap-2">
                <Input
                  value={capabilityInput}
                  onChange={(e) => setCapabilityInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCapability())}
                  placeholder="e.g., Write professional emails"
                  className="max-w-md"
                />
                <Button type="button" variant="outline" size="sm" onClick={handleAddCapability}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.capabilities.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.capabilities.map((cap, idx) => (
                    <Badge key={idx} variant="outline">
                      {cap}
                      <button type="button" onClick={() => handleRemoveCapability(cap)} className="ml-2 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Requirements */}
            <div className="space-y-4">
              <h3 className="text-base font-medium">Training Requirements</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium">Text Training</span>
                  </div>
                  <Switch
                    checked={formData.requirements.text}
                    onCheckedChange={(val) => setFormData({
                      ...formData,
                      requirements: { ...formData.requirements, text: val }
                    })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg opacity-50">
                  <div className="flex items-center gap-2">
                    <Mic className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-medium">Voice Training (Coming Soon)</span>
                  </div>
                  <Switch checked={false} disabled />
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg opacity-50">
                  <div className="flex items-center gap-2">
                    <Video className="w-5 h-5 text-pink-600" />
                    <span className="text-sm font-medium">Video Training (Coming Soon)</span>
                  </div>
                  <Switch checked={false} disabled />
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Behavior Tab */}
        <TabsContent value="behavior">
          <Card className="p-6 space-y-6">
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold">Skill Behavior</h2>
                <p className="text-sm text-gray-600">Define how your skill thinks and responds</p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="systemPrompt">System Prompt</Label>
                  <span className="text-xs text-gray-500">
                    {promptData.systemPrompt.length}/6000 characters
                  </span>
                </div>
                <Textarea
                  id="systemPrompt"
                  value={promptData.systemPrompt}
                  onChange={(e) => setPromptData({ ...promptData, systemPrompt: e.target.value })}
                  placeholder="Define the core behavior and identity of your skill. Example: 'You are an expert email composer who writes clear, professional emails...'"
                  rows={8}
                  className={promptData.systemPrompt.length > 6000 ? 'border-red-500' : ''}
                  data-testid="system-prompt-input"
                />
                <p className="text-xs text-gray-500">
                  The system prompt defines how your skill behaves. Be specific about tone, expertise, and constraints.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="jobDescription">Job Description</Label>
                <Textarea
                  id="jobDescription"
                  value={promptData.jobDescription}
                  onChange={(e) => setPromptData({ ...promptData, jobDescription: e.target.value })}
                  placeholder="Describe what this skill does in detail. Example: 'Help users compose professional emails for various scenarios including follow-ups, introductions, and requests...'"
                  rows={4}
                  data-testid="job-description-input"
                />
                <p className="text-xs text-gray-500">
                  Explain what tasks this skill handles and what the user can expect.
                </p>
              </div>
            </div>

            {/* Output Constraints */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-base font-medium">Output Settings</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tone</Label>
                  <Select 
                    value={promptData.outputConstraints.tone || 'professional'}
                    onValueChange={(val) => setPromptData({
                      ...promptData,
                      outputConstraints: { ...promptData.outputConstraints, tone: val }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                      <SelectItem value="friendly">Friendly</SelectItem>
                      <SelectItem value="formal">Formal</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Max Words (optional)</Label>
                  <Input
                    type="number"
                    value={promptData.outputConstraints.maxWords || ''}
                    onChange={(e) => setPromptData({
                      ...promptData,
                      outputConstraints: { 
                        ...promptData.outputConstraints, 
                        maxWords: e.target.value ? parseInt(e.target.value) : null 
                      }
                    })}
                    placeholder="No limit"
                  />
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules">
          <Card className="p-6 space-y-6">
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold">Execution Rules</h2>
                <p className="text-sm text-gray-600">Define specific rules your skill must follow</p>
              </div>
              
              {/* Priority Rules */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Priority Rules</Label>
                  <span className="text-xs text-gray-500">{promptData.priorityRules.length}/20</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={ruleInput}
                    onChange={(e) => setRuleInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRule())}
                    placeholder="e.g., Always include a call-to-action"
                    className="flex-1"
                    disabled={promptData.priorityRules.length >= 20}
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={handleAddRule}
                    disabled={promptData.priorityRules.length >= 20}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {promptData.priorityRules.length > 0 && (
                  <div className="space-y-2">
                    {promptData.priorityRules.map((rule, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <span className="text-xs text-gray-400 w-6">{idx + 1}.</span>
                        <span className="flex-1 text-sm">{rule}</span>
                        <button 
                          type="button"
                          onClick={() => handleRemoveRule(rule)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Format Rules */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <Label>Format Rules</Label>
                  <span className="text-xs text-gray-500">{promptData.outputConstraints.formatRules.length}/20</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={formatRuleInput}
                    onChange={(e) => setFormatRuleInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFormatRule())}
                    placeholder="e.g., Use bullet points for lists"
                    className="flex-1"
                    disabled={promptData.outputConstraints.formatRules.length >= 20}
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={handleAddFormatRule}
                    disabled={promptData.outputConstraints.formatRules.length >= 20}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {promptData.outputConstraints.formatRules.length > 0 && (
                  <div className="space-y-2">
                    {promptData.outputConstraints.formatRules.map((rule, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <span className="text-xs text-gray-400 w-6">{idx + 1}.</span>
                        <span className="flex-1 text-sm">{rule}</span>
                        <button 
                          type="button"
                          onClick={() => handleRemoveFormatRule(rule)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Preview Tab */}
        {isEditing && (
          <TabsContent value="preview">
            <Card className="p-6 space-y-6">
              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-semibold">Test Your Skill</h2>
                  <p className="text-sm text-gray-600">
                    Send a test message to see how your skill responds with the current settings.
                    Changes don&apos;t need to be saved to test.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Test Message</Label>
                  <Textarea
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    placeholder="Enter a test message to see how your skill responds..."
                    rows={3}
                    data-testid="test-message-input"
                  />
                </div>
                
                <Button 
                  onClick={handlePreview}
                  disabled={previewLoading || !testMessage.trim()}
                  data-testid="preview-btn"
                >
                  {previewLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <PlayCircle className="w-4 h-4 mr-2" />
                      Run Test
                    </>
                  )}
                </Button>
                
                {/* Preview Result */}
                {previewResult && (
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Response received</span>
                      <Badge variant="outline" className="text-xs">
                        Using {previewResult.promptUsed} prompt
                      </Badge>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg border">
                      <div className="prose prose-sm max-w-none">
                        {previewResult.response}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Leave Confirmation Dialog */}
      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowLeaveDialog(false);
              setPendingNavigation(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                setShowLeaveDialog(false);
                if (pendingNavigation) {
                  navigate(pendingNavigation);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Leave Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
